import { Dispatch, SetStateAction, useEffect, useState } from 'react'

export function useLocalStorageState<ValueType>(
  storageKey: string,
  initialValue: ValueType,
  expirationTime?: number,
): [ValueType, Dispatch<SetStateAction<ValueType>>] {
  function isPrevStateFunction(
    setStateAction: SetStateAction<ValueType>,
  ): setStateAction is (prevState?: ValueType) => ValueType {
    return typeof setStateAction === 'function'
  }

  const getLocalStorageValue = () => {
    const valueString = window.localStorage.getItem(storageKey)
    if (valueString) {
      const parsedValue = JSON.parse(valueString)

      if (expirationTime && parsedValue.timestamp) {
        const isExpired = Date.now() - parsedValue.timestamp > expirationTime
        if (isExpired) {
          window.localStorage.removeItem(storageKey)
          return null
        }
      }

      return parsedValue.value
    }
    return null
  }

  const [value, setValue] = useState<ValueType>(getLocalStorageValue() ?? initialValue)

  const handleSetValue: Dispatch<SetStateAction<ValueType>> = setStateAction => {
    let newValue = setStateAction
    if (isPrevStateFunction(setStateAction)) {
      newValue = setStateAction(value)
    }

    const valueToStore = {
      value: newValue,
      timestamp: Date.now(),
    }
    window.localStorage.setItem(storageKey, JSON.stringify(valueToStore))

    setValue(newValue)
  }

  useEffect(() => {
    const storageValue = getLocalStorageValue()
    if (storageValue !== null && storageValue !== value) {
      handleSetValue(storageValue)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey])

  return [value, handleSetValue]
}
